import React, { useState } from 'react';
import SPAPill from '../shared/SPAPill';
import FilterPill from './FilterPill';
import { MetricCard, MetricCardSkeleton } from './Metric';
import useSyncOverview from './useSyncOverview';
import SPASectionHeading from '../shared/SPASectionHeading';

const WINDOWS = ['24h', '7d', '30d'];

const PlatformHealthTab = () => {
  const [window, setWindow] = useState('24h');
  const { data, error } = useSyncOverview(window);

  const totals = data?.totals;

  return (
    <>
      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        {WINDOWS.map(value => (
          <FilterPill key={value} active={window === value} onClick={() => setWindow(value)}>
            {`Last ${value}`}
          </FilterPill>
        ))}
      </div>

      {/* The distinction the whole page rests on — a run can be 100% successful and deliver nothing,
          because a Blueprint course accepts every post and discards it. */}
      <p className="tw-mb-4 tw-mt-0 tw-max-w-3xl tw-text-sm tw-text-spa-secondary">
        Whether sync calls are getting through, across every district. Not the same question as whether a
        teacher&apos;s current scores are in the LMS — that one starts from the teacher, on the Teachers tab.
      </p>

      {error != null && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error">Couldn&apos;t load the overview.</p>
      )}

      {data == null && error == null && (
        <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => <MetricCardSkeleton key={index} />)}
        </div>
      )}

      {totals != null && (
        <>
          <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
            <MetricCard
              hint="Calls that reached the LMS"
              label="Attempts"
              value={totals.attempted.toLocaleString()}
            />
            <MetricCard
              hint="2xx responses"
              label="Accepted"
              value={totals.accepted.toLocaleString()}
            />
            <MetricCard
              label="Attempt success rate"
              value={totals.success_rate == null ? '—' : `${totals.success_rate}%`}
            />
            <MetricCard
              hint="Our own limiter — never reached the LMS, so not an error rate"
              label="Refused before sending"
              value={totals.refused_locally.toLocaleString()}
            />
          </div>

          <SPASectionHeading title="By HTTP status" />
          <div className="tw-mb-6 tw-flex tw-flex-wrap tw-gap-2">
            {Object.entries(data.by_status).map(([status, count]) => (
              <SPAPill key={status} tone={Number(status) < 300 ? 'success' : 'error'}>
                {`${status} · ${count.toLocaleString()}`}
              </SPAPill>
            ))}
          </div>

          <SPASectionHeading title="By platform" />
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {Object.entries(data.by_platform).map(([platform, count]) => (
              <SPAPill key={platform} tone="neutral">
                {`${platform} · ${count.toLocaleString()}`}
              </SPAPill>
            ))}
          </div>
          {/* lms_type is 'lti' for ~871k of 1.1M rows, merging Canvas and Schoology. */}
          <p className="tw-mt-2 tw-text-sm tw-text-spa-secondary">
            Platform here is the raw lms_type, which records Canvas and Schoology both as
            &ldquo;lti&rdquo;. The Troubleshoot table splits them by AGS URL.
          </p>
        </>
      )}
    </>
  );
};

export default PlatformHealthTab;
